"use client";

import { useRef } from "react";
import { Compass, Mountain, Hammer, Sprout } from "lucide-react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const steps = [{
  number: "01",
  title: "Request Service",
  body: "Submit your repair request through our marketplace form. Provide details on your appliance type, symptoms, and preferred timing.",
  Icon: Mountain
}, {
  number: "02",
  title: "Pro Matching",
  body: "We match your request with independent local technicians in the Atlanta area who service your specific appliance brand and issue.",
  Icon: Compass
}, {
  number: "03",
  title: "Diagnostic & Quote",
  body: "A local pro evaluates the unit, diagnoses the mechanical problem, and provides their quote directly before starting repair work.",
  Icon: Hammer
}, {
  number: "04",
  title: "Job Completion",
  body: "The technician completes the service using appropriate replacement components, ensuring your household system is back up and running.",
  Icon: Sprout
}];
export default function Process() {
  const container = useRef<HTMLElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add({
      motion: "(prefers-reduced-motion: no-preference)",
      reduced: "(prefers-reduced-motion: reduce)"
    }, context => {
      const {
        motion
      } = context.conditions as {
        motion: boolean;
      };
      gsap.from(".method-eyebrow", {
        opacity: 0,
        y: motion ? 12 : 0,
        duration: motion ? 0.48 : 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 80%"
        }
      });
      gsap.from(".method-heading", {
        opacity: 0,
        y: motion ? 20 : 0,
        duration: motion ? 0.64 : 0.2,
        delay: motion ? 0.12 : 0,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 80%"
        }
      });
      gsap.from(".method-lead", {
        opacity: 0,
        y: motion ? 16 : 0,
        duration: motion ? 0.56 : 0.2,
        delay: motion ? 0.24 : 0,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container.current,
          start: "top 80%"
        }
      });
      const stepEls = gsap.utils.toArray<HTMLElement>(".method-step");
      stepEls.forEach((step, i) => {
        const row = Math.floor(i / 2);
        const inRow = i % 2;
        const numeral = step.querySelector(".step-numeral");
        const heading = step.querySelector(".step-heading");
        const body = step.querySelector(".step-body");
        const icon = step.querySelector(".step-icon");
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: step,
            start: "top 82%"
          },
          delay: motion ? inRow * 0.12 : 0
        });
        tl.from([icon, numeral], {
          opacity: 0,
          y: motion ? 12 : 0,
          duration: motion ? 0.48 : 0.2,
          ease: "power2.out",
          stagger: motion ? 0.05 : 0
        }).from(heading, {
          opacity: 0,
          y: motion ? 16 : 0,
          duration: motion ? 0.56 : 0.2,
          ease: "power2.out"
        }, motion ? "-=0.38" : 0).from(body, {
          opacity: 0,
          y: motion ? 14 : 0,
          duration: motion ? 0.56 : 0.2,
          ease: "power2.out"
        }, motion ? "-=0.46" : 0);
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="24689" id="appliancerepairprocess" ref={container} data-nav="light" className="bg-background py-24 md:py-44 text-foreground">
      <div className="mx-auto max-w-[1360px] px-6 md:px-10">
        {}
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-8">
            <div className="method-eyebrow flex items-center gap-2">
              <span aria-hidden="true" className="inline-block h-[10px] w-[1.5px]" style={{
              backgroundColor: "var(--primary)"
            }} />
              <span className="font-default text-xs font-semibold uppercase md:text-sm" style={{
              color: "var(--accent-foreground)",
              letterSpacing: "0.22em"
            }}>
                Our Simple Process
              </span>
              <span aria-hidden="true" className="inline-block h-[10px] w-[1.5px]" style={{
              backgroundColor: "var(--primary)"
            }} />
            </div>

            <h2 className="method-heading mt-6 font-mono uppercase text-foreground" style={{
            fontSize: "clamp(2.5rem, 6vw, 5.75rem)",
            fontWeight: 700,
            letterSpacing: "-0.005em",
            lineHeight: 0.95
          }}>
              Four Steps.
              <br />
              Fixed Fast.
            </h2>
          </div>

          <div className="lg:col-span-4 lg:pt-4">
            <p className="method-lead font-default text-muted-foreground" style={{
            fontSize: "clamp(1rem, 1.05vw, 1.0625rem)",
            lineHeight: 1.65,
            maxWidth: "60ch"
          }}>
              Getting help for a broken refrigerator, washer, or oven shouldn't be stressful.
              Handy Heroes connects Atlanta homeowners with experienced local contractors who inspect, quote, and complete the work.
            </p>
          </div>
        </div>

        {}
        <div className="mt-20 md:mt-24">
          <div className="grid grid-cols-1 gap-x-10 gap-y-14 md:grid-cols-12 md:gap-y-16">
            {steps.map((step, i) => {
            const Icon = step.Icon;
            const isRowBreakBefore = i === 2;
            return <div key={step.number} data-index={i} className={["method-step md:col-span-6", isRowBreakBefore ? "md:pt-14 md:border-t md:border-border" : "", i === 3 ? "md:pt-14 md:border-t md:border-border" : ""].join(" ")}>
                  <div className="flex items-start gap-5 md:gap-6">
                    {}
                    <div className="step-icon flex-shrink-0 pt-2">
                      <Icon className="h-6 w-6" strokeWidth={1.5} style={{
                    color: "var(--primary)"
                  }} aria-hidden="true" />
                    </div>

                    {}
                    <div className="flex-1">
                      <div className="flex items-center gap-4">
                        <span className="step-numeral font-mono" style={{
                      color: "var(--primary)",
                      fontSize: "3rem",
                      fontWeight: 700,
                      lineHeight: 1
                    }}>
                          {step.number}
                        </span>
                        <span aria-hidden="true" className="inline-block h-6 w-[1.5px]" style={{
                      backgroundColor: "var(--primary)"
                    }} />
                        <h3 className="step-heading font-mono uppercase text-foreground" style={{
                      fontSize: "clamp(1.25rem, 2vw, 2rem)",
                      fontWeight: 600,
                      letterSpacing: "0.01em",
                      lineHeight: 1.05
                    }}>
                          {step.title}
                        </h3>
                      </div>

                      <p className="step-body mt-4 font-default text-muted-foreground" style={{
                    fontSize: "clamp(1rem, 1.05vw, 1.0625rem)",
                    lineHeight: 1.65,
                    maxWidth: "52ch"
                  }}>
                        {step.body}
                      </p>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}